import React from "react";

import {
  FaEdit,
  FaFileInvoice,
  FaFileSignature,
  FaTrash,
} from "react-icons/fa";

import {
  BillingItemTypeBadge,
  LifecycleStatusBadge,
  PaymentStatusBadge,
  RenewalStatusBadge,
} from "../ui/SubscriptionBadge";

import {
  calculateSubscriptionProgress,
} from "../utils/subscriptionDates";

import {
  formatSubscriptionDate,
  formatSubscriptionMoney,
  getAssociationName,
} from "../utils/subscriptionFormatters";

function getPaymentPercentage(subscription) {
  const total = Number(
    subscription.total_amount || 0,
  );

  const paid = Number(
    subscription.amount_paid || 0,
  );

  if (total <= 0) return 100;

  return Math.min(
    Math.max((paid / total) * 100, 0),
    100,
  );
}

function SubscriptionProgress({
  subscription,
}) {
  if (subscription.item_type === "one_time") {
    return (
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Réalisée le{" "}
        {formatSubscriptionDate(
          subscription.start_date,
        )}
      </p>
    );
  }

  const progress =
    calculateSubscriptionProgress({
      itemType: subscription.item_type,
      startDate: subscription.start_date,
      endDate: subscription.end_date,
    });

  if (!progress) {
    return (
      <p className="text-xs text-slate-400">
        Période incomplète
      </p>
    );
  }

  return (
    <div className="mt-2">
      <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
        <span>
          {Math.round(progress.percentage)} %
        </span>

        <span>
          {progress.isExpired
            ? "Expiré"
            : `${progress.daysRemaining} j restants`}
        </span>
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
        <div
          className={[
            "h-full rounded-full",
            progress.isExpired
              ? "bg-red-500"
              : progress.percentage >= 85
                ? "bg-orange-500"
                : "bg-indigo-500",
          ].join(" ")}
          style={{
            width: `${progress.percentage}%`,
          }}
        />
      </div>
    </div>
  );
}

function PaymentProgress({
  subscription,
}) {
  const percentage =
    getPaymentPercentage(subscription);

  return (
    <div className="mt-2">
      <div className="mb-1 flex justify-between text-[11px] text-slate-500 dark:text-slate-400">
        <span>
          Réglé :{" "}
          {formatSubscriptionMoney(
            subscription.amount_paid,
          )}
        </span>

        <span>{Math.round(percentage)} %</span>
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
        <div
          className={[
            "h-full rounded-full",
            subscription.is_overdue
              ? "bg-red-500"
              : subscription.is_paid
                ? "bg-emerald-500"
                : "bg-sky-500",
          ].join(" ")}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function DocumentButton({
  href,
  title,
  icon,
}) {
  if (!href) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={title}
      className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 text-slate-500 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600 dark:border-slate-700 dark:text-slate-300 dark:hover:border-indigo-800 dark:hover:bg-indigo-950/30"
    >
      {icon}
    </a>
  );
}

export default function SubscriptionsDesktopTable({
  subscriptions = [],
  isLoading = false,
  deletingId = null,
  onEdit,
  onDelete,
}) {
  if (isLoading) {
    return (
      <div className="hidden space-y-2 p-5 lg:block">
        {[1, 2, 3, 4, 5].map((item) => (
          <div
            key={item}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  if (!subscriptions.length) {
    return (
      <div className="hidden min-h-[260px] place-items-center p-6 text-center lg:grid">
        <div>
          <p className="text-lg font-bold text-slate-700 dark:text-slate-200">
            Aucun résultat
          </p>

          <p className="mt-1 text-sm text-slate-400">
            Modifie les filtres ou ajoute une
            nouvelle vente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="hidden overflow-x-auto lg:block">
      <table className="min-w-[1320px] table-fixed">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left dark:border-slate-800 dark:bg-slate-950/50">
            <th className="w-[245px] px-5 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
              Client / produit
            </th>

            <th className="w-[170px] px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
              Type
            </th>

            <th className="w-[190px] px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
              Montant
            </th>

            <th className="w-[220px] px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
              Période
            </th>

            <th className="w-[190px] px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
              Paiement
            </th>

            <th className="w-[180px] px-4 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">
              Renouvellement
            </th>

            <th className="w-[160px] px-5 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">
              Actions
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {subscriptions.map((subscription) => (
            <tr
              key={subscription.id}
              className="bg-white transition hover:bg-slate-50/80 dark:bg-slate-900 dark:hover:bg-slate-800/50"
            >
              <td className="px-5 py-4 align-top">
                <p className="truncate font-black text-slate-950 dark:text-white">
                  {getAssociationName(subscription)}
                </p>

                <p className="mt-1 truncate text-sm font-medium text-slate-600 dark:text-slate-300">
                  {subscription.product}
                </p>

                <div className="mt-2">
                  <LifecycleStatusBadge
                    status={subscription.status}
                  />
                </div>
              </td>

              <td className="px-4 py-4 align-top">
                <BillingItemTypeBadge
                  itemType={subscription.item_type}
                />

                <p className="mt-2 text-xs text-slate-400">
                  Réf. #{subscription.id}
                </p>
              </td>

              <td className="px-4 py-4 align-top">
                <p className="font-black text-slate-950 dark:text-white">
                  {formatSubscriptionMoney(
                    subscription.total_amount,
                  )}
                </p>

                <PaymentProgress
                  subscription={subscription}
                />
              </td>

              <td className="px-4 py-4 align-top">
                {subscription.item_type ===
                "one_time" ? (
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    {formatSubscriptionDate(
                      subscription.start_date,
                    )}
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatSubscriptionDate(
                        subscription.start_date,
                      )}
                    </p>

                    <p className="mt-1 text-sm font-bold text-slate-800 dark:text-slate-200">
                      →{" "}
                      {formatSubscriptionDate(
                        subscription.end_date,
                      )}
                    </p>
                  </>
                )}

                <SubscriptionProgress
                  subscription={subscription}
                />
              </td>

              <td className="px-4 py-4 align-top">
                <PaymentStatusBadge
                  status={
                    subscription.payment_status
                  }
                />

                {!subscription.is_paid && (
                  <p
                    className={[
                      "mt-2 text-xs font-semibold",
                      subscription.is_overdue
                        ? "text-red-600 dark:text-red-300"
                        : "text-slate-500 dark:text-slate-400",
                    ].join(" ")}
                  >
                    Reste{" "}
                    {formatSubscriptionMoney(
                      subscription.remaining_amount,
                    )}
                  </p>
                )}

                {subscription.payment_due_date && (
                  <p className="mt-1 text-xs text-slate-400">
                    Échéance{" "}
                    {formatSubscriptionDate(
                      subscription.payment_due_date,
                    )}
                  </p>
                )}
              </td>

              <td className="px-4 py-4 align-top">
                {subscription.item_type ===
                "subscription" ? (
                  <RenewalStatusBadge
                    status={
                      subscription.renewal_status
                    }
                  />
                ) : (
                  <span className="text-xs italic text-slate-400">
                    Non applicable
                  </span>
                )}
              </td>

              <td className="px-5 py-4 align-top">
                <div className="flex justify-end gap-2">
                  <DocumentButton
                    href={
                      subscription.drive_quote_link
                    }
                    title="Ouvrir le devis"
                    icon={<FaFileSignature />}
                  />

                  <DocumentButton
                    href={
                      subscription.drive_invoice_link
                    }
                    title="Ouvrir la facture"
                    icon={<FaFileInvoice />}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      onEdit?.(subscription)
                    }
                    title="Modifier"
                    className="grid h-9 w-9 place-items-center rounded-xl bg-indigo-50 text-indigo-600 transition hover:bg-indigo-600 hover:text-white dark:bg-indigo-950/40 dark:text-indigo-300"
                  >
                    <FaEdit />
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      onDelete?.(subscription)
                    }
                    disabled={
                      Number(deletingId) ===
                      Number(subscription.id)
                    }
                    title="Supprimer"
                    className="grid h-9 w-9 place-items-center rounded-xl bg-red-50 text-red-600 transition hover:bg-red-600 hover:text-white disabled:cursor-wait disabled:opacity-50 dark:bg-red-950/40 dark:text-red-300"
                  >
                    <FaTrash />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}